import { MouseEvent, useEffect, useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/core/Select';
import { useOptionTranslator } from '@/components/datamodel/hooks/useOptionTranslator';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getCustomFilter } from '@/services/angularjs-bridge';
import { cn } from '@/services/utils';
import {
    DataModelAttribute,
    DataModelAttributeOption,
    PersistedItem,
} from '@/types/DataModel';

interface HitListDimensionalReadProps {
    attribute: DataModelAttribute;
    item: PersistedItem;
    editable: boolean;
    activeDimension?: string;
    onActiveDimensionChange: (key: string) => void;
    onEdit: () => void;
}

const optionKey = (option: DataModelAttributeOption) => option.key ?? option.name ?? '';

const dimensionValue = (item: PersistedItem, attributeName: string, key?: string) => {
    if (!key) return '';
    const hash = item[attributeName];
    if (!hash || typeof hash !== 'object' || Array.isArray(hash)) return '';
    const value = (hash as Record<string, unknown>)[key];
    return value == null ? '' : String(value);
};

/**
 * Browse hit-list read view for a dimensional attribute (Simple Language).
 * The language menu is always usable. The text stays read-only until a
 * double-click.
 */
const HitListDimensionalRead = ({
    attribute,
    item,
    editable,
    activeDimension,
    onActiveDimensionChange,
    onEdit,
}: HitListDimensionalReadProps) => {
    const { dataModel } = useDataModel();
    const { organization } = useOrganization();
    const { user } = useUser();
    const { translateOption } = useOptionTranslator();
    const [options, setOptions] = useState<DataModelAttributeOption[]>([]);

    useEffect(() => {
        const keys = attribute.params?.keys;
        if (!dataModel || keys == null) return;
        let alive = true;

        const applyFilter = (loaded: DataModelAttributeOption[]) => {
            const filterName = attribute.params?.uiCodelistFilter || attribute.params?.uiOptionsFilter;
            const filterFn = filterName ? getCustomFilter(filterName) : undefined;
            if (!filterFn || !user || !organization) return loaded;
            const filteredKeys = filterFn(
                loaded.map(optionKey),
                item,
                attribute,
                user,
                organization,
                undefined,
            ) as string[] | undefined;
            if (!Array.isArray(filteredKeys) || filteredKeys.length === 0) return loaded;
            const byKey = new Map(loaded.map((option) => [optionKey(option), option]));
            return filteredKeys
                .map((key) => byKey.get(key))
                .filter((option): option is DataModelAttributeOption => option != null);
        };

        if (Array.isArray(keys)) {
            setOptions(applyFilter(keys as unknown as DataModelAttributeOption[]));
            return () => {
                alive = false;
            };
        }

        const groupNames = (attribute.params as { keysOptionGroups?: string[] } | undefined)?.keysOptionGroups ?? [];
        const list = dataModel.optionList(keys);
        if (Array.isArray(list?.options) && list.options.length > 0 && list.optionsLoaded !== false) {
            setOptions(applyFilter(list.options));
            return () => {
                alive = false;
            };
        }

        dataModel.optionListOptionsAsync(keys, groupNames).then((loaded) => {
            if (alive) setOptions(applyFilter(loaded ?? []));
        });
        return () => {
            alive = false;
        };
    }, [attribute, dataModel, item, organization, user]);

    useEffect(() => {
        if (!options.length) return;
        if (activeDimension && options.some((option) => optionKey(option) === activeDimension)) return;
        const hash = item[attribute.name];
        const firstValueKey = hash && typeof hash === 'object' && !Array.isArray(hash)
            ? Object.keys(hash)[0]
            : undefined;
        const match = options.find((option) => optionKey(option) === firstValueKey);
        const next = optionKey(match ?? options[0]);
        if (next) onActiveDimensionChange(next);
    }, [options, activeDimension, item, attribute.name, onActiveDimensionChange]);

    const text = dimensionValue(item, attribute.name, activeDimension);

    const keepReadMode = (event: MouseEvent) => {
        event.stopPropagation();
    };

    return (
        <div className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-items-center">
            <div
                className="dimensional-dimension-select tw-w-20 tw-shrink-0"
                onClick={keepReadMode}
                onDoubleClick={keepReadMode}
            >
                {activeDimension && (
                    <Select value={activeDimension} onValueChange={onActiveDimensionChange}>
                        <SelectTrigger
                            borderless
                            className="tw-h-full tw-w-full tw-bg-gray-100 tw-text-t-primary"
                        >
                            <span className="tw-font-normal tw-text-t-sm lg:tw-text-t-base tw-text-t-primary">{activeDimension}</span>
                        </SelectTrigger>
                        <SelectContent className="tw-w-[500px] tw-max-h-[400px] tw-overflow-y-auto !tw-border !tw-border-solid !tw-border-[#e9ebec] !tw-shadow-none">
                            {options.map((option) => {
                                const key = optionKey(option);
                                if (!key) return null;
                                return (
                                    <SelectItem key={key} value={key}>
                                        <span className="tw-font-bold">
                                            {translateOption(key, attribute, options)}
                                        </span>
                                        <span className="tw-mx-2">|</span>
                                        <span className="tw-truncate">
                                            {dimensionValue(item, attribute.name, key)}
                                        </span>
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                )}
            </div>
            <div
                data-hit-list-editable={editable ? 'true' : undefined}
                title={text || undefined}
                tabIndex={0}
                onDoubleClick={(event) => {
                    event.stopPropagation();
                    if (!editable) return;
                    onEdit();
                }}
                className={cn(
                    'hit-list-cell tw-box-border tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-items-center tw-truncate',
                    'tw-rounded tw-border tw-border-solid tw-border-transparent tw-pl-2 tw-pr-2',
                    'tw-text-t-sm lg:tw-text-t-base tw-text-t-primary',
                    editable && 'hit-list-editable-affordance',
                )}
            >
                {text}
            </div>
        </div>
    );
};

export { HitListDimensionalRead };
